import { api } from "@/services/auth";

export type GeralModuleCard = {
  id: string;
  label: string;
  color: string;
  bgLight: string;
  activeCount: number;
  clientCount: number;
  searchFields: { value: string; label: string }[];
  pipeline: { label: string; value: number; href: string }[];
  newProcessHref: string;
};

export type GeralPipelineStage = {
  label: string;
  value: number;
  href: string;
  variant: string;
  bg?: string;
  color?: string;
  borderColor?: string;
};

export type GeralCalendarEvent = {
  date: number;
  module: "marcas" | "patentes" | "di" | "internacional";
  type: string;
  description: string;
};

export type GeralDashboardData = {
  statusLabel: string;
  monthLabel: string;
  pipeline: {
    stages: GeralPipelineStage[];
    parallelStages: GeralPipelineStage[];
    finalStages: GeralPipelineStage[];
  };
  moduleCards: GeralModuleCard[];
  calendarEvents: GeralCalendarEvent[];
  noticias: Array<{ title: string; date: string; tag: string }>;
  comunicados: Array<{ title: string; date: string; tag: string }>;
};

export type GeralCrmClientFunnelStageKey = "atendimento" | "em-negociacao" | "ganho" | "perdido";

// Pipeline stage styles — CommercialPipeline.tsx expects bg, color, borderColor inline style props
const PIPELINE_STYLES: Record<string, { bg: string; color: string; borderColor: string }> = {
  Atendimento: { bg: "rgba(27,58,92,0.06)", color: "#1B3A5C", borderColor: "rgba(27,58,92,0.16)" },
  "Em Atendimento": { bg: "rgba(27,58,92,0.06)", color: "#1B3A5C", borderColor: "rgba(27,58,92,0.16)" },
  "Em Negociação": { bg: "rgba(232,114,26,0.08)", color: "#E8721A", borderColor: "rgba(232,114,26,0.18)" },
  "Negócios Fechados": { bg: "rgba(15,138,95,0.08)", color: "#0F8A5F", borderColor: "rgba(15,138,95,0.18)" },
  "Negócios Perdidos": { bg: "rgba(194,65,93,0.08)", color: "#C2415D", borderColor: "rgba(194,65,93,0.18)" },
  "Propostas Aceitas": { bg: "rgba(27,58,92,0.04)", color: "#1B3A5C", borderColor: "rgba(27,58,92,0.12)" },
  "Formalização": { bg: "rgba(232,114,26,0.05)", color: "#E8721A", borderColor: "rgba(232,114,26,0.14)" },
  "Execução": { bg: "rgba(27,58,92,0.08)", color: "#1B3A5C", borderColor: "rgba(27,58,92,0.18)" },
  "Em Recebimento": { bg: "rgba(232,114,26,0.05)", color: "#E8721A", borderColor: "rgba(232,114,26,0.14)" },
  "Concluído": { bg: "rgba(27,58,92,0.04)", color: "#1B3A5C", borderColor: "rgba(27,58,92,0.12)" },
};

const CRM_CLIENT_FUNNEL_STAGE_META: Record<
  GeralCrmClientFunnelStageKey,
  Pick<GeralPipelineStage, "label" | "href" | "variant">
> = {
  atendimento: {
    label: "Atendimento",
    href: "/comercial/atendimentos?etapa=lead",
    variant: "bg-muted text-foreground",
  },
  "em-negociacao": {
    label: "Em Negociação",
    href: "/comercial/atendimentos?etapa=em-negociacao",
    variant: "bg-primary/10 text-primary",
  },
  ganho: {
    label: "Negócios Fechados",
    href: "/comercial/atendimentos?etapa=ganho",
    variant: "bg-success/10 text-success",
  },
  perdido: {
    label: "Negócios Perdidos",
    href: "/comercial/atendimentos?etapa=perdido",
    variant: "bg-danger/10 text-danger",
  },
};

function normalizePipelineText(value?: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function createCrmClientFunnelStage(
  key: GeralCrmClientFunnelStageKey,
  value = 0,
): GeralPipelineStage {
  const meta = CRM_CLIENT_FUNNEL_STAGE_META[key];
  return applyPipelineStyles({
    ...meta,
    value,
  });
}

function mapCrmClientFunnelStageKey(
  stage?: Pick<GeralPipelineStage, "label" | "href"> | null,
): GeralCrmClientFunnelStageKey | undefined {
  const label = normalizePipelineText(stage?.label);
  const href = normalizePipelineText(stage?.href);

  if (label.includes("perdido") || href.includes("perdido")) return "perdido";
  if (
    label.includes("ganho") ||
    label.includes("fechado") ||
    label.includes("concluido") ||
    href.includes("ganho")
  ) {
    return "ganho";
  }
  if (
    label.includes("negociacao") ||
    href.includes("em-negociacao") ||
    href.includes("negociacao")
  ) {
    return "em-negociacao";
  }
  if (label.includes("atendimento") || href.includes("lead")) return "atendimento";
  return undefined;
}

export function getGeralCrmClientFunnelStages(
  pipeline?: Partial<GeralDashboardData["pipeline"]> | null,
): GeralPipelineStage[] {
  const sourceStages = Array.isArray(pipeline?.stages) ? pipeline.stages : [];
  const grouped = new Map<GeralCrmClientFunnelStageKey, GeralPipelineStage>(
    (Object.keys(CRM_CLIENT_FUNNEL_STAGE_META) as GeralCrmClientFunnelStageKey[]).map((key) => [
      key,
      createCrmClientFunnelStage(key),
    ]),
  );

  for (const stage of sourceStages) {
    const key = mapCrmClientFunnelStageKey(stage);
    if (!key) continue;
    const current = grouped.get(key);
    if (!current) continue;
    current.value += Number(stage.value || 0);
  }

  return (Object.keys(CRM_CLIENT_FUNNEL_STAGE_META) as GeralCrmClientFunnelStageKey[]).map(
    (key) => grouped.get(key) as GeralPipelineStage,
  );
}

export function getGeralCrmClientFunnelStageValue(
  pipeline: Partial<GeralDashboardData["pipeline"]> | null | undefined,
  key: GeralCrmClientFunnelStageKey,
) {
  return getGeralCrmClientFunnelStages(pipeline).find((stage) => mapCrmClientFunnelStageKey(stage) === key)?.value || 0;
}

function buildGeralCrmClientFunnel(
  pipeline?: Partial<GeralDashboardData["pipeline"]> | null,
): GeralDashboardData["pipeline"] {
  return {
    stages: getGeralCrmClientFunnelStages(pipeline),
    parallelStages: [],
    finalStages: [],
  };
}

function applyPipelineStyles(stage: GeralPipelineStage): GeralPipelineStage {
  const style = PIPELINE_STYLES[stage.label];
  if (style) {
    return { ...stage, bg: stage.bg || style.bg, color: stage.color || style.color, borderColor: stage.borderColor || style.borderColor };
  }
  return stage;
}

// Empty state — no fake data. Module card structure kept but with zero counts.
const fallbackData: GeralDashboardData = {
  statusLabel: "Sistema operacional",
  monthLabel: new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(new Date()),
  pipeline: buildGeralCrmClientFunnel(),
  moduleCards: [
    {
      id: "marcas",
      label: "Marcas",
      color: "text-blue-500",
      bgLight: "bg-blue-50",
      activeCount: 0,
      clientCount: 0,
      searchFields: [
        { value: "processo", label: "Processo" },
        { value: "cliente", label: "Cliente" },
        { value: "marca", label: "Nome da Marca" },
        { value: "classe", label: "Classe NCL" },
        { value: "titular", label: "Titular" },
        { value: "natureza", label: "Natureza" },
      ],
      pipeline: [
        { label: "Leads", value: 0, href: "/comercial?modulo=marcas&etapa=lead" },
        { label: "Clientes", value: 0, href: "/cadastros?modulo=marcas" },
        { label: "Pesquisa", value: 0, href: "/marcas/operacional?fase=pesquisa" },
        { label: "Elaboração", value: 0, href: "/marcas/operacional?fase=elaboracao" },
        { label: "Protocoladas", value: 0, href: "/marcas/operacional?fase=protocolada" },
        { label: "Monitoramento", value: 0, href: "/marcas/operacional?fase=monitoramento" },
      ],
      newProcessHref: "/marcas/operacional",
    },
    {
      id: "patentes",
      label: "Patentes",
      color: "text-amber-500",
      bgLight: "bg-amber-50",
      activeCount: 0,
      clientCount: 0,
      searchFields: [
        { value: "pedido", label: "Pedido" },
        { value: "protocolo", label: "Protocolo" },
        { value: "cliente", label: "Cliente" },
        { value: "inventor", label: "Inventor" },
        { value: "titular", label: "Titular" },
        { value: "titulo", label: "Título" },
        { value: "resumo", label: "Resumo" },
      ],
      pipeline: [
        { label: "Leads", value: 0, href: "/comercial?modulo=patentes&etapa=lead" },
        { label: "Clientes", value: 0, href: "/cadastros?modulo=patentes" },
        { label: "Pesquisa", value: 0, href: "/patentes/monitor" },
        { label: "Redação", value: 0, href: "/patentes/monitor" },
        { label: "Depositadas", value: 0, href: "/patentes/monitor" },
        { label: "Monitoramento", value: 0, href: "/patentes/monitor" },
      ],
      newProcessHref: "/patentes/monitor",
    },
    {
      id: "di",
      label: "Desenho Industrial",
      color: "text-teal-500",
      bgLight: "bg-teal-50",
      activeCount: 0,
      clientCount: 0,
      searchFields: [
        { value: "processo", label: "Processo" },
        { value: "cliente", label: "Cliente" },
        { value: "referencia", label: "Referência" },
        { value: "categoria", label: "Categoria" },
        { value: "titular", label: "Titular" },
      ],
      pipeline: [
        { label: "Leads", value: 0, href: "/comercial?modulo=di&etapa=lead" },
        { label: "Clientes", value: 0, href: "/cadastros?modulo=di" },
        { label: "Pesquisa", value: 0, href: "/di/operacional?fase=pesquisa" },
        { label: "Preparação", value: 0, href: "/di/operacional?fase=preparacao" },
        { label: "Protocolados", value: 0, href: "/di/operacional?fase=protocolado" },
        { label: "Monitoramento", value: 0, href: "/di/operacional?fase=monitoramento" },
      ],
      newProcessHref: "/di/operacional",
    },
    {
      id: "internacional",
      label: "Internacional",
      color: "text-violet-500",
      bgLight: "bg-violet-50",
      activeCount: 0,
      clientCount: 0,
      searchFields: [
        { value: "processo", label: "Processo" },
        { value: "cliente", label: "Cliente" },
        { value: "pais", label: "País" },
        { value: "titulo", label: "Título" },
        { value: "protocolo", label: "Protocolo" },
        { value: "titular", label: "Titular" },
      ],
      pipeline: [
        { label: "Leads", value: 0, href: "/comercial?modulo=internacional&etapa=lead" },
        { label: "Clientes", value: 0, href: "/cadastros?modulo=internacional" },
        { label: "Pesquisa", value: 0, href: "/internacional?fase=pesquisa" },
        { label: "Preparação", value: 0, href: "/internacional?fase=preparacao" },
        { label: "Protocolados", value: 0, href: "/internacional?fase=protocolado" },
        { label: "Monitoramento", value: 0, href: "/internacional?fase=monitoramento" },
      ],
      newProcessHref: "/internacional",
    },
  ],
  calendarEvents: [],
  noticias: [],
  comunicados: [],
};

type ComercialDashboardApi = {
  counters?: {
    open_total?: number;
    qualificacao?: number;
    negociacao?: number;
    ganho?: number;
    perdido?: number;
    lead?: number;
    proposta?: number;
  };
  opportunities?: {
    items?: Array<{
      domain?: string;
    }>;
  };
};

type DomainDashboardApi = {
  kpis?: {
    ativos?: number;
    total_processos?: number;
  };
};

type AlertApi = {
  domain?: string;
  title?: string;
  due_date?: string | null;
  alert_type?: string;
};

function monthLabelFromNow() {
  const formatter = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" });
  const raw = formatter.format(new Date());
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function mapAlertType(label?: string) {
  if (label?.includes("estagnada")) return "Alerta";
  if (label?.includes("prazo")) return "Prazo";
  return "Exigência";
}

function mapDomainModule(domain?: string): GeralCalendarEvent["module"] {
  if (domain === "marcas") return "marcas";
  if (domain === "di") return "di";
  if (domain === "internacional") return "internacional";
  return "patentes";
}

/**
 * Merge backend moduleCards (which have id, label, activeCount, clientCount, pipelineCounts)
 * with the rich fallback card definitions (searchFields, pipeline, color, bgLight, etc.)
 */
function mergeModuleCards(backendCards: Array<Record<string, any>>): GeralModuleCard[] {
  // Map pipeline label → pipelineCounts key
  const labelToCountKey: Record<string, string> = {
    leads: "leads", clientes: "clientes", pesquisa: "pesquisa",
    "elaboração": "elaboracao", elaboracao: "elaboracao",
    protocoladas: "protocoladas", protocolados: "protocolados",
    monitoramento: "monitoramento", "redação": "redacao", redacao: "redacao",
    depositadas: "depositadas", "preparação": "preparacao", preparacao: "preparacao",
  };

  return fallbackData.moduleCards.map((fallbackCard) => {
    const backendCard = backendCards.find((bc) => bc.id === fallbackCard.id);
    if (!backendCard) return fallbackCard;
    const counts = backendCard.pipelineCounts || {};

    // Merge pipeline stage values from backend counts
    const pipeline = fallbackCard.pipeline.map((stage) => {
      const normalizedLabel = stage.label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const key = labelToCountKey[normalizedLabel] || normalizedLabel;
      const backendValue = counts[key];
      return {
        ...stage,
        value: backendValue !== undefined ? Number(backendValue) : stage.value,
      };
    });

    return {
      ...fallbackCard,
      activeCount: Number(backendCard.activeCount ?? fallbackCard.activeCount),
      clientCount: Number(backendCard.clientCount ?? fallbackCard.clientCount),
      pipeline,
    };
  });
}

function adaptDashboard(payload: unknown): GeralDashboardData {
  if (!payload || typeof payload !== "object") return fallbackData;
  const obj = payload as Record<string, any>;

  // Merge moduleCards: backend only sends {id, label, activeCount, clientCount}
  // We must preserve the frontend-only properties (searchFields, pipeline, color, bgLight, newProcessHref)
  const moduleCards = Array.isArray(obj.moduleCards)
    ? mergeModuleCards(obj.moduleCards)
    : fallbackData.moduleCards;

  // Apply inline style props to pipeline stages so CommercialPipeline can render them
  const rawPipeline = obj.pipeline;
  const pipeline = buildGeralCrmClientFunnel(rawPipeline);

  // Normalize calendarEvents.module to lowercase (backend sends domain as uppercase sometimes)
  const calendarEvents = Array.isArray(obj.calendarEvents)
    ? obj.calendarEvents.map((e: any) => ({
        date: Number(e.date ?? 1),
        module: mapDomainModule(String(e.module || "").toLowerCase()),
        type: String(e.type || "Prazo"),
        description: String(e.description || ""),
      }))
    : fallbackData.calendarEvents;

  return {
    statusLabel: String(obj.statusLabel ?? fallbackData.statusLabel),
    monthLabel: String(obj.monthLabel ?? fallbackData.monthLabel),
    pipeline,
    moduleCards,
    calendarEvents,
    noticias: Array.isArray(obj.noticias) ? obj.noticias : fallbackData.noticias,
    comunicados: Array.isArray(obj.comunicados) ? obj.comunicados : fallbackData.comunicados,
  };
}

export const geralService = {
  getFallback: (): GeralDashboardData => fallbackData,
  getDashboard: async (): Promise<GeralDashboardData> => {
    try {
      // Try unified dashboard endpoint first (real data)
      const unified = await api.get("/v2/geral/dashboard").catch(() => null);
      if (unified?.data) {
        return adaptDashboard(unified.data);
      }
    } catch { /* fall through to legacy */ }

    try {
      // Fallback: Each call fails independently — if marcas errors, patentes still works
      const safe = (p: Promise<any>) => p.catch(() => ({ data: {} }));
      const [comercialRes, patentesRes, marcasRes, diRes, internacionalRes, alertasRes] = await Promise.all([
        safe(api.get("/v2/comercial/dashboard")),
        safe(api.get("/v2/patentes/dashboard")),
        safe(api.get("/v2/marcas/dashboard")),
        safe(api.get("/v2/di/dashboard")),
        safe(api.get("/v2/internacional/dashboard")),
        safe(api.get("/v2/alertas")),
      ]);
      const comercial = (comercialRes.data || {}) as ComercialDashboardApi;
      const patentes = (patentesRes.data || {}) as DomainDashboardApi;
      const marcas = (marcasRes.data || {}) as DomainDashboardApi;
      const di = (diRes.data || {}) as DomainDashboardApi;
      const internacional = (internacionalRes.data || {}) as DomainDashboardApi;
      const alertas = Array.isArray(alertasRes.data?.items) ? (alertasRes.data.items as AlertApi[]) : [];

      const opportunities = Array.isArray(comercial.opportunities?.items) ? comercial.opportunities?.items : [];
      const domainCounter = new Map<string, number>();
      for (const row of opportunities) {
        const domain = String(row?.domain || "patentes");
        domainCounter.set(domain, (domainCounter.get(domain) || 0) + 1);
      }
      const monthLabel = monthLabelFromNow();
      const moduleCards = fallbackData.moduleCards.map((card) => {
        if (card.id === "marcas") {
          return {
            ...card,
            activeCount: Number(marcas.kpis?.ativos || 0),
            clientCount: Number(domainCounter.get("marcas") || card.clientCount),
          };
        }
        if (card.id === "di") {
          return {
            ...card,
            activeCount: Number(di.kpis?.ativos || 0),
            clientCount: Number(domainCounter.get("di") || card.clientCount),
          };
        }
        if (card.id === "internacional") {
          return {
            ...card,
            activeCount: Number(internacional.kpis?.ativos || 0),
            clientCount: Number(domainCounter.get("internacional") || card.clientCount),
          };
        }
        return {
          ...card,
          activeCount: Number(patentes.kpis?.ativos || 0),
          clientCount: Number(domainCounter.get("patentes") || card.clientCount),
        };
      });
      const topAlerts = alertas
        .filter((item) => item?.due_date)
        .slice(0, 10)
        .map((item) => {
          const due = item.due_date ? new Date(item.due_date) : null;
          return {
            date: due && !Number.isNaN(due.getTime()) ? due.getDate() : 1,
            module: mapDomainModule(item.domain),
            type: mapAlertType(item.alert_type),
            description: String(item.title || "Alerta operacional"),
          } as GeralCalendarEvent;
        });
      const counters = comercial.counters || {};
      return {
        statusLabel: "Sistema operacional",
        monthLabel,
        pipeline: buildGeralCrmClientFunnel({
          stages: [
            createCrmClientFunnelStage("atendimento", Number(counters.lead || 0)),
            createCrmClientFunnelStage(
              "em-negociacao",
              Number(counters.qualificacao || 0) + Number(counters.proposta || 0) + Number(counters.negociacao || 0),
            ),
            createCrmClientFunnelStage("ganho", Number(counters.ganho || 0)),
            createCrmClientFunnelStage("perdido", Number(counters.perdido || 0)),
          ],
        }),
        moduleCards,
        calendarEvents: topAlerts,
        noticias: [],
        comunicados: [],
      };
    } catch {
      return fallbackData;
    }
  },
};
